import React, { useCallback, useEffect, useState } from "react";
import { Volume2, X } from "lucide-react";
import { toast } from "sonner";

// OwnerAudioPanel — owner-only knob for the station-wide loudness target
// + a one-click backfill action that runs ffmpeg's `loudnorm` filter
// against every previously-uploaded video that hasn't been analysed yet.
// Sits in OwnerBar's SITE MESSAGES dropdown for parity with the other
// admin tools and persists changes via /api/site/audio-settings.

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const TARGET_MIN = -30;
const TARGET_MAX = -10;
const DEFAULT_TARGET = -16;

async function fetchSettings() {
  const res = await fetch(`${API}/site/audio-settings`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function fetchStatus(token) {
  const res = await fetch(`${API}/owner/loudness-status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Reference reading for the slider — common targets explained in plain
// English so the owner has a frame of reference instead of guessing.
const REFERENCE_LEVELS = [
  { value: -14, label: "Music streaming (loud)" },
  { value: -16, label: "Default · Web / Podcast" },
  { value: -23, label: "Broadcast TV (EBU R128)" },
];

export default function OwnerAudioPanel({ isOwner, getToken, onClose }) {
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [draft, setDraft] = useState(DEFAULT_TARGET);
  const [status, setStatus] = useState({ total: 0, analysed: 0, unmeasurable: 0, pending: 0, last_update_at: null, recent: [] });
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [loading, setLoading] = useState(true);
  // Live ETA computed from the rate at which the analysed count is
  // climbing across recent polls. `eta.ms` is null until we've seen
  // enough movement to make a guess. `eta.ratePerMin` is the smoothed
  // tapes-per-minute throughput.
  const [eta, setEta] = useState({ ms: null, ratePerMin: null });
  // Bumped every second while the panel is open so the relative
  // "Last update Xs ago" string ticks in real time (the status poll
  // is every 4 s, which is too coarse to feel live for short intervals).
  const [agoTick, setAgoTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setAgoTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const [s, st] = await Promise.all([
        fetchSettings(),
        fetchStatus(token),
      ]);
      if (typeof s?.target_lufs === "number") {
        setTarget(s.target_lufs);
        setDraft(s.target_lufs);
      }
      const safe = st || { total: 0, analysed: 0, unmeasurable: 0, pending: 0, last_update_at: null, recent: [] };
      setStatus(safe);
      // Auto-resume the polling spinner if the analyser has touched a
      // tape within the last 20 s and there's still pending work.
      // Lets the owner reload the page mid-backfill and immediately
      // pick up where they left off instead of seeing a stale snapshot.
      if (safe.last_update_at && (safe.pending || 0) > 0) {
        const age = Date.now() - new Date(safe.last_update_at).getTime();
        if (Number.isFinite(age) && age < 20_000) setBackfilling(true);
      }
    } catch (e) {
      toast.error("AUDIO FETCH FAILED", { description: e.message });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isOwner) return;
    loadAll();
  }, [isOwner, loadAll]);

  // Refresh the status counter periodically while work is in flight so
  // the owner can see progress without re-opening the panel. Also acts
  // as a watchdog: if the backend worker stalls (production worker
  // recycled, OOM, deploy, network hiccup) and `last_update_at` doesn't
  // move for ~30 s while pending > 0, automatically re-trigger
  // /loudness-backfill — the endpoint is idempotent, so it picks up
  // whatever's still pending. The catalogue self-heals through worker
  // deaths without the owner having to babysit the panel.
  useEffect(() => {
    if (!backfilling) {
      setEta({ ms: null, ratePerMin: null });
      return undefined;
    }
    let stableCount = 0;
    // Grant a startup grace period equal to the cooldown so the
    // watchdog doesn't re-kick within the first 30 s of a fresh
    // backfill — the worker hasn't even queued its first download yet.
    let lastResumeAt = Date.now();
    const samples = [];
    const WINDOW = 6;
    // STALL_MS is generous because a single 2-4 GB tape can take
    // 60-180 s to download + analyse, during which `last_update_at`
    // doesn't move. The backend now self-chains batches inside one
    // kick, so under normal draining the watchdog should never fire —
    // this threshold only catches genuine worker death / OOM kills.
    const STALL_MS = 180_000;
    const RESUME_COOLDOWN_MS = 60_000;
    const t = setInterval(async () => {
      try {
        const token = typeof getToken === "function" ? getToken() : "";
        const st = await fetchStatus(token);
        setStatus(st);
        // Rolling-window ETA estimate.
        const now = Date.now();
        samples.push({ t: now, analysed: st.analysed || 0 });
        while (samples.length > WINDOW) samples.shift();
        if (samples.length >= 2 && (st.pending || 0) > 0) {
          const first = samples[0];
          const last = samples[samples.length - 1];
          const deltaTapes = last.analysed - first.analysed;
          const deltaSec = (last.t - first.t) / 1000;
          if (deltaTapes > 0 && deltaSec > 0) {
            const ratePerMin = (deltaTapes / deltaSec) * 60;
            const etaMs = (st.pending / (deltaTapes / deltaSec)) * 1000;
            setEta({ ms: etaMs, ratePerMin });
          }
        } else if ((st.pending || 0) === 0) {
          setEta({ ms: 0, ratePerMin: null });
        }
        // Watchdog: if there's pending work but no analyser activity
        // for the last STALL_MS, re-kick. Throttled by RESUME_COOLDOWN_MS
        // so a single dead worker doesn't get spammed.
        const lastTouched = st.last_update_at ? new Date(st.last_update_at).getTime() : 0;
        const idle = lastTouched ? (now - lastTouched) : Infinity;
        if ((st.pending || 0) > 0 && idle > STALL_MS && now - lastResumeAt > RESUME_COOLDOWN_MS) {
          lastResumeAt = now;
          try {
            await fetch(`${API}/owner/loudness-backfill`, {
              method: "POST",
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            toast.message("BACKFILL RESUMED", { description: "Watchdog re-kicked a stalled worker" });
          } catch (e) { /* try again next tick */ }
        }
        // Stable-count auto-stop: only fires once pending hits 0 OR
        // the analyser has been quiet AND the watchdog has given up
        // (i.e. pending is 0 — which the watchdog won't kick).
        const isMoving = samples.length >= 2 && samples[samples.length - 1].analysed !== samples[0].analysed;
        if (!isMoving && (st.pending || 0) === 0) {
          stableCount += 1;
          if (stableCount >= 3) setBackfilling(false);
        } else {
          stableCount = 0;
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [backfilling, getToken]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const res = await fetch(`${API}/site/audio-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ target_lufs: draft }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const out = await res.json();
      setTarget(out.target_lufs);
      setDraft(out.target_lufs);
      // Tell App.js to refetch + apply the new gain across the cabinet
      // immediately (no page reload required).
      window.dispatchEvent(new Event("crt:audio-settings-updated"));
      toast.success("AUDIO TARGET SAVED", { description: `${out.target_lufs} LUFS station-wide` });
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const triggerBackfill = async () => {
    if (backfilling || status.pending === 0) return;
    setBackfilling(true);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const res = await fetch(`${API}/owner/loudness-backfill`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const out = await res.json();
      toast.success("BACKFILL QUEUED", { description: `${out.queued} tape(s) — runs in background` });
    } catch (e) {
      toast.error("BACKFILL FAILED", { description: e.message });
      setBackfilling(false);
    }
  };

  // Retry the unmeasurable bucket — tapes the analyser previously
  // failed on (silent / un-decodable). The backend clears their
  // `lufs_attempted_at` mark before re-running so they get a clean
  // re-evaluation; tapes that fail again get re-marked.
  const triggerRetryUnmeasurable = async () => {
    if (backfilling || (status.unmeasurable || 0) === 0) return;
    setBackfilling(true);
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      const res = await fetch(`${API}/owner/loudness-retry-unmeasurable`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const out = await res.json();
      toast.success("RETRY QUEUED", { description: `${out.queued} unmeasurable tape(s) — runs in background` });
    } catch (e) {
      toast.error("RETRY FAILED", { description: e.message });
      setBackfilling(false);
    }
  };

  if (!isOwner) return null;

  const dirty = draft !== target;
  const pctAnalysed = status.total > 0 ? Math.round((status.analysed / status.total) * 100) : 0;

  // Format an elapsed-ms estimate into a tight "Xm Ys" / "Ys" / "<1s"
  // string for the running ETA pill. Returns null on missing input
  // (samples haven't stabilised yet).
  const formatEta = (ms) => {
    if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return null;
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return `${m}m ${rem}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };
  const etaText = formatEta(eta.ms);
  const rateText = (eta.ratePerMin && eta.ratePerMin > 0)
    ? `${eta.ratePerMin.toFixed(1)}/min`
    : null;
  // Format the most-recent analyser touch as a relative-time string.
  // Re-evaluates every second via `agoTick` so it ticks live between
  // 4-second status polls. Null when no tape has ever been touched.
  const formatAgo = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const s = Math.floor(ms / 1000);
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  };
  // Reference `agoTick` so React re-renders this when the ticker bumps.
  const lastUpdateText = formatAgo(status.last_update_at) || (agoTick && null);

  return (
    <div
      className="absolute top-12 right-0 w-[420px] bg-black border-2 border-[var(--phosphor)] text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in_srgb,var(--phosphor)_35%,transparent)]"
      data-testid="audio-panel"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="text-xs tracking-[0.3em] flex items-center gap-2">
          <Volume2 size={14} />
          ┌─ AUDIO ─┐
        </div>
        <button onClick={onClose} className="text-[var(--phosphor)]" data-testid="audio-close" aria-label="close">
          <X size={14} />
        </button>
      </div>

      <div className="px-4 pb-3 text-[10px] opacity-70 leading-relaxed">
        Station-wide loudness target. Every uploaded tape gets a per-video
        gain so quiet cartoons and loud trailers play at the same volume.
      </div>

      {loading ? (
        <div className="text-center text-[11px] opacity-60 py-6">LOADING…</div>
      ) : (
        <>
          <div className="px-4 pb-3">
            <div className="flex items-end justify-between mb-1">
              <span className="text-[10px] tracking-[0.25em] opacity-75">TARGET LUFS</span>
              <span
                className="text-[18px] tabular-nums font-bold leading-none"
                style={{ fontFamily: "VT323, monospace" }}
                data-testid="audio-target-value"
              >
                {draft.toFixed(1)}
              </span>
            </div>
            <input
              type="range"
              min={TARGET_MIN}
              max={TARGET_MAX}
              step={0.5}
              value={draft}
              onChange={(e) => setDraft(Number(e.target.value))}
              data-testid="audio-target-slider"
              className="w-full accent-[var(--phosphor)]"
            />
            <div className="flex justify-between text-[9px] opacity-50 mt-1">
              <span>{TARGET_MIN} (quiet)</span>
              <span>{TARGET_MAX} (loud)</span>
            </div>

            {/* Reference legend — clickable buttons that snap the slider
                to a common target. Keeps the owner from having to guess
                what the numbers mean. */}
            <div className="mt-2 space-y-1">
              {REFERENCE_LEVELS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setDraft(r.value)}
                  data-testid={`audio-ref-${r.value}`}
                  className={`w-full text-left text-[10px] px-2 py-1 border transition-colors ${
                    draft === r.value
                      ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]"
                      : "border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] hover:border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)]"
                  }`}
                >
                  <span className="tabular-nums opacity-85 mr-2">{r.value}</span>
                  <span className="opacity-65">{r.label}</span>
                </button>
              ))}
            </div>

            <button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="audio-save"
              className="mt-3 w-full border border-[var(--phosphor)] py-1.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {saving ? "SAVING…" : dirty ? "APPLY" : "SAVED"}
            </button>
          </div>

          <div className="border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] px-4 py-3">
            <div className="text-[10px] tracking-[0.25em] opacity-75 mb-2">┌─ BACKFILL ─┐</div>
            <div className="text-[10px] opacity-70 leading-relaxed mb-2">
              Re-analyse uploads that don&apos;t yet have a LUFS reading. Runs
              in the background — safe to close this panel.
            </div>
            <div
              className="text-[11px] tabular-nums mb-2"
              data-testid="audio-backfill-status"
            >
              <span className="opacity-70">ANALYSED:</span>{" "}
              <span className="font-bold">{status.analysed}</span>
              <span className="opacity-40"> / </span>
              <span className="font-bold">{status.total}</span>
              <span className="opacity-50 ml-2">({pctAnalysed}%)</span>
              {(status.unmeasurable || 0) > 0 && (
                <span
                  className="ml-2 text-[#FF6B35] opacity-90"
                  title="Tapes the analyser couldn't measure (silent / un-decodable audio)"
                >
                  · {status.unmeasurable} N/A
                </span>
              )}
              {backfilling && status.pending > 0 && (
                <span className="ml-2 text-[#FFCC00] animate-pulse">⟳ RUNNING</span>
              )}
              {backfilling && etaText && (
                <span
                  className="ml-2 text-[var(--phosphor)] opacity-85"
                  data-testid="audio-backfill-eta"
                  title={rateText ? `Throughput: ${rateText}` : "Estimated time to completion"}
                >
                  ETA ~{etaText}
                </span>
              )}
              {lastUpdateText && !backfilling && (
                <span
                  className="ml-2 opacity-55"
                  data-testid="audio-last-update"
                  title={`Last analyser activity: ${status.last_update_at}`}
                >
                  · {lastUpdateText}
                </span>
              )}
            </div>
            {/* Progress bar — a phosphor strip that fills as videos are
                analysed in the background. */}
            <div className="h-1.5 bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] mb-3">
              <div
                className="h-full bg-[var(--phosphor)] shadow-[0_0_6px_var(--phosphor)] transition-[width] duration-500"
                style={{ width: `${pctAnalysed}%` }}
              />
            </div>
            <button
              onClick={triggerBackfill}
              disabled={backfilling || status.pending === 0}
              data-testid="audio-backfill"
              className="w-full border border-[var(--phosphor)] py-1.5 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              {status.pending === 0
                ? "ALL TAPES ANALYSED"
                : backfilling
                  ? `ANALYSING ${status.pending}…`
                  : `BACKFILL ${status.pending} TAPE${status.pending === 1 ? "" : "S"}`}
            </button>

            {/* RETRY UNMEASURABLE — second pass against the bucket the
                analyser previously couldn't measure. Useful after you
                re-encode a bad-audio source or fix the upload pipeline.
                Hidden when there are no unmeasurable tapes so the panel
                stays tidy. */}
            {(status.unmeasurable || 0) > 0 && (
              <button
                onClick={triggerRetryUnmeasurable}
                disabled={backfilling}
                data-testid="audio-retry-unmeasurable"
                title="Re-run analysis against tapes the analyser couldn't measure on the first pass"
                className="mt-2 w-full border border-[#FF6B35] text-[#FF6B35] py-1.5 text-[10px] tracking-widest hover:bg-[#FF6B35] hover:text-black transition-colors disabled:opacity-40"
              >
                {backfilling
                  ? `RETRYING ${status.unmeasurable}…`
                  : `↻ RETRY ${status.unmeasurable} N/A TAPE${status.unmeasurable === 1 ? "" : "S"}`}
              </button>
            )}

            <div className="text-[9px] opacity-50 mt-2 leading-snug">
              Notes: YouTube + archive tapes can&apos;t be measured (we don&apos;t
              control their audio). Silent uploads stay unmeasurable.
            </div>

            {/* Recent-analysis mini-log — the last 5 tapes the worker
                touched, newest first. Gives the owner a real signal that
                the worker is making progress (titles + LUFS readings)
                instead of just an incrementing counter. */}
            {(status.recent || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]">
                <div className="text-[10px] tracking-[0.25em] opacity-75 mb-1.5">┌─ RECENT ─┐</div>
                <ul className="space-y-0.5 text-[10px]" data-testid="audio-recent-log">
                  {status.recent.map((r) => {
                    const measured = typeof r.lufs === "number";
                    const reading = measured ? `${r.lufs.toFixed(1)} LUFS` : "N/A";
                    const tone = measured ? "text-[var(--phosphor)]" : "text-[#FF6B35]";
                    return (
                      <li
                        key={r.id}
                        className="flex items-center gap-2"
                        data-testid={`audio-recent-${r.id}`}
                      >
                        <span className="opacity-50 tabular-nums shrink-0 w-12 text-[9px]">
                          {formatAgo(r.lufs_attempted_at) || ""}
                        </span>
                        <span className="opacity-80 truncate flex-1" title={r.title}>
                          {r.title || "(no title)"}
                        </span>
                        <span className={`${tone} tabular-nums shrink-0 text-[9px]`}>
                          {reading}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
